import { forwardRef, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import useMediaQuery from "@mui/material/useMediaQuery";
import Barcode from "react-barcode";
import { redeemPromotion } from "../../../api/homeApi";

// Breakpoint for the stacked/compact layouts below — matches MUI's "sm".
const COMPACT_QUERY = "(max-width:599.95px)";

// The detail sheet always slides up from the bottom (an "action sheet"),
// not a centered dialog with side margins — the standard mobile pattern,
// and it reads fine on wider screens too since width is capped there.
const SlideUpTransition = forwardRef(function SlideUpTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EMPTY_ARRAY = [];

const ensureString = (value) => {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return String(value);
};

const escapeHtml = (value) =>
  ensureString(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const isLikelyHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(ensureString(value));

const normalizeQuickOfferArray = (payload) => {
  const candidates = [
    payload?.data?.offers,
    payload?.data?.items,
    payload?.data?.quick_offers,
    payload?.data?.quickOffers,
    payload?.offers,
    payload?.items,
    payload?.quick_offers,
    payload?.quickOffers,
    payload?.data,
    payload,
  ];

  const match = candidates.find((item) => Array.isArray(item));
  return Array.isArray(match) ? match : EMPTY_ARRAY;
};

const normalizeQuickOfferItem = (item, index) => {
  if (typeof item === "string") {
    return {
      id: `coupon-${index}`,
      title: "",
      promoCode: "",
      validity: "",
      content: item,
      link: "",
    };
  }

  if (!item || typeof item !== "object") {
    return {
      id: `coupon-${index}`,
      title: "",
      promoCode: "",
      validity: "",
      content: "",
      link: "",
    };
  }

  return {
    id: ensureString(item.uuid || item.id || item.offer_uuid || index),
    title: ensureString(
      item.title ||
        item.subject ||
        item.name ||
        item.offer_title ||
        item.heading,
    ),
    promoCode: ensureString(
      item.promo_code ||
        item.promoCode ||
        item.coupon_code ||
        item.couponCode ||
        item.code ||
        item.offer_code,
    ),
    validity: ensureString(
      item.validity ||
        item.valid_till ||
        item.validTill ||
        item.valid_to ||
        item.validTo ||
        item.expiry ||
        item.expiry_date ||
        item.expiryDate ||
        item.expires_at ||
        item.expiresAt,
    ),
    content: ensureString(
      item.content ||
        item.offer_content ||
        item.description ||
        item.message ||
        item.html ||
        item.body ||
        item.offer ||
        item.text,
    ),
    link: ensureString(item.link || item.url || item.redirect_url),
    isActive: item.is_active !== undefined ? item.is_active : item.isActive,
  };
};

const enrichAnchors = (html) => {
  const value = ensureString(html).trim();
  if (!value) return "";

  if (
    typeof window !== "undefined" &&
    typeof window.DOMParser !== "undefined"
  ) {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    doc.querySelectorAll("a").forEach((anchor) => {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    });
    return doc.body.innerHTML;
  }

  return value.replace(
    /<a\b(?![^>]*\btarget=)([^>]*)>/gi,
    '<a$1 target="_blank" rel="noopener noreferrer">',
  );
};

const linkifyPlainText = (value, fallbackLink) => {
  const text = ensureString(value).trim();
  if (!text && !fallbackLink) return "";

  if (isLikelyHtml(text)) {
    return enrichAnchors(text);
  }

  const safeText = escapeHtml(text).replace(/\r?\n/g, "<br />");
  const withInlineLinks = safeText.replace(
    /(https?:\/\/[^\s<]+)/gi,
    (match) =>
      `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`,
  );

  if (!text && fallbackLink) {
    const safeLink = escapeHtml(fallbackLink);
    return `<a href="${safeLink}" target="_blank" rel="noopener noreferrer">${safeLink}</a>`;
  }

  if (
    fallbackLink &&
    !withInlineLinks.includes("href=") &&
    !text.includes(fallbackLink)
  ) {
    const safeLink = escapeHtml(fallbackLink);
    return `${withInlineLinks}<br /><a href="${safeLink}" target="_blank" rel="noopener noreferrer">${safeLink}</a>`;
  }

  return withInlineLinks;
};

const renderStatusCard = (icon, heading, description, isLoading = false) => (
  <div
    style={{
      width: "100%",
      maxWidth: 920,
      minHeight: 190,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      padding: "28px 20px",
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: 58,
        height: 58,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        background: "rgba(46, 125, 50, 0.05)",
        border: "1px solid rgba(46, 125, 50, 0.18)",
        color: "#43a047",
      }}
    >
      {isLoading ? <CircularProgress size={28} thickness={4.8} /> : icon}
    </div>
    <p
      style={{
        margin: 0,
        fontSize: 16,
        fontWeight: 600,
        color: "#344054",
      }}
    >
      {heading}
    </p>
    <p
      style={{
        margin: 0,
        fontSize: 14,
        color: "#667085",
      }}
    >
      {description}
    </p>
  </div>
);

const formatValidity = (value) => {
  const text = ensureString(value).trim();
  if (!text) return "-";
  const normalizedText = text
    .replace(/^\s*-\s*to\s*/i, "")
    .replace(/^\s*to\s*/i, "")
    .replace(/^\s*-\s*/i, "")
    .trim();
  const dateOnly =
    normalizedText.match(/\d{4}-\d{2}-\d{2}/)?.[0] || normalizedText;

  return `Valid upto ${dateOnly}`;
};

// Same look as the admin "Customer-Facing Preview" cards: flat colour per card
// (cycled by index) with a blurred coupon-doodle overlay and the store logo on
// the right.
const DOODLE_SVG = `
  <svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>
    <g fill='none' stroke='#ffffff' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' opacity='0.22'>
      <g transform='translate(18,24) rotate(-10)'>
        <circle cx='0' cy='0' r='3'/>
        <circle cx='12' cy='12' r='3'/>
        <line x1='14' y1='-2' x2='-2' y2='14'/>
      </g>
      <g transform='translate(98,12) rotate(16)'>
        <path d='M0 0 L12 0 L12 12 L2 22 L-10 10 Z'/>
        <circle cx='5' cy='5' r='2'/>
      </g>
      <g transform='translate(134,58) rotate(-8)'>
        <path d='M0 0 L16 0 L18 21 L-2 21 Z'/>
        <path d='M4 0 Q4 -8 8 -8 Q12 -8 12 0'/>
      </g>
      <g transform='translate(24,74) rotate(6)'>
        <line x1='0' y1='0' x2='6' y2='0' stroke-dasharray='2 3'/>
        <line x1='10' y1='0' x2='16' y2='0' stroke-dasharray='2 3'/>
        <line x1='20' y1='0' x2='26' y2='0' stroke-dasharray='2 3'/>
      </g>
      <g transform='translate(70,42) rotate(24) scale(0.8)'>
        <circle cx='0' cy='0' r='3'/>
        <circle cx='12' cy='12' r='3'/>
        <line x1='14' y1='-2' x2='-2' y2='14'/>
      </g>
      <g transform='translate(122,98) rotate(-16)'>
        <path d='M0 0 L12 0 L12 12 L2 22 L-10 10 Z'/>
        <circle cx='5' cy='5' r='2'/>
      </g>
      <g transform='translate(8,108) rotate(12)'>
        <path d='M0 0 L16 0 L18 21 L-2 21 Z'/>
        <path d='M4 0 Q4 -8 8 -8 Q12 -8 12 0'/>
      </g>
      <g transform='translate(92,128) rotate(-6)'>
        <line x1='0' y1='0' x2='6' y2='0' stroke-dasharray='2 3'/>
        <line x1='10' y1='0' x2='16' y2='0' stroke-dasharray='2 3'/>
        <line x1='20' y1='0' x2='26' y2='0' stroke-dasharray='2 3'/>
      </g>
      <g transform='translate(44,138) rotate(18)'>
        <circle cx='0' cy='0' r='3'/>
        <circle cx='12' cy='12' r='3'/>
        <line x1='14' y1='-2' x2='-2' y2='14'/>
      </g>
      <g transform='translate(4,148) rotate(-9) scale(0.85)'>
        <path d='M0 0 L12 0 L12 12 L2 22 L-10 10 Z'/>
        <circle cx='5' cy='5' r='2'/>
      </g>
    </g>
  </svg>
`;
const DOODLE_BG = `url("data:image/svg+xml,${encodeURIComponent(DOODLE_SVG)}")`;

const CARD_THEMES = [
  { background: "#e8590c", accent: "#c2410c" },
  { background: "#1c1c1e", accent: "#111827" },
  { background: "#1971c2", accent: "#1e3a8a" },
  { background: "#2f9e44", accent: "#1a7431" },
];

const hasValue = (value) => value !== null && value !== undefined;

const getPromoHeadline = (item) => {
  const type = item.promotion_type;
  if (type === "percent_discount" && hasValue(item.discount_percent)) {
    return { eyebrow: "Up to", headline: `${item.discount_percent}% OFF` };
  }
  if (type === "amount_discount" && hasValue(item.discount_amount)) {
    return {
      eyebrow: "Get up to",
      headline: `$${Number(item.discount_amount).toFixed(2)} OFF`,
    };
  }
  if (type === "sale_price" && hasValue(item.sale_price)) {
    return {
      eyebrow: "Now only",
      headline: `$${Number(item.sale_price).toFixed(2)}`,
    };
  }
  if (type === "promo_code") {
    return { eyebrow: "Special offer", headline: "Use code below" };
  }
  return { eyebrow: "Special offer", headline: "Limited time" };
};

const normalizePromotionGroups = (payload) => {
  const groups = payload?.data;
  return Array.isArray(groups)
    ? groups.filter(
        (group) => Array.isArray(group?.items) && group.items.length,
      )
    : EMPTY_ARRAY;
};

const PromotionGroups = ({ groups, storeImage, onSelect }) => (
  <>
    {groups.map((group) => (
      <div key={group.category_uuid} style={{ marginBottom: 20 }}>
        <p
          style={{
            margin: "0 0 10px",
            fontSize: 16,
            fontWeight: 700,
            color: "#1f2937",
          }}
        >
          {group.category_name}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 12,
          }}
        >
          {group.items.map((item, index) => {
            const theme = CARD_THEMES[index % CARD_THEMES.length];
            const { eyebrow, headline } = getPromoHeadline(item);
            return (
              <div
                key={item.uuid}
                onClick={() => onSelect({ item, theme, headline, eyebrow })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect({ item, theme, headline, eyebrow });
                  }
                }}
                role="button"
                tabIndex={0}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 16,
                  backgroundColor: theme.background,
                  color: "#fff",
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: -6,
                    backgroundImage: DOODLE_BG,
                    backgroundRepeat: "repeat",
                    filter: "blur(2.5px)",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{ fontSize: 12, fontWeight: 600, opacity: 0.85 }}
                    >
                      {eyebrow}
                    </div>
                    <div
                      style={{
                        fontSize: 19,
                        fontWeight: 800,
                        marginTop: 1,
                        lineHeight: 1.15,
                      }}
                    >
                      {headline}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.85,
                        marginTop: 3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.description || item.product_name}
                    </div>
                    {item.promo_code ? (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          background: "#fff",
                          color: theme.accent,
                          borderRadius: 999,
                          padding: "3px 10px",
                          marginTop: 8,
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {item.promo_code}
                      </div>
                    ) : null}
                  </div>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "1px solid #eee",
                    }}
                  >
                    {storeImage ? (
                      <img
                        src={storeImage}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <LocalOfferOutlinedIcon
                        sx={{ fontSize: 20, color: "#9ca3af" }}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </>
);

PromotionGroups.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  storeImage: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

const conditionLines = (terms) =>
  ensureString(terms)
    .split(/\r?\n|;/)
    .map((line) => line.trim())
    .filter(Boolean);

const formatLongDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const sectionTitleStyle = { fontSize: 13, fontWeight: 700, marginBottom: 8 };
const listStyle = { margin: 0, paddingLeft: 18 };
const listItemStyle = { fontSize: 13, color: "#6b7280" };

// "idle" | "sending" | "sent" | "expired" | "already_redeemed" |
// "max_attempts" | "error" — the last two are terminal (no retry).
const TERMINAL_REDEEM_STATES = new Set(["already_redeemed", "max_attempts"]);

// Tap Redeem to notify the store; they have 1 minute to confirm on-site
// (see PromotionRedemption / expireOverduePending on the backend) before
// the request expires and the countdown lets the customer try again, up
// to the server-enforced attempt cap. Keyed by promotion uuid from the
// caller so switching promos always starts this fresh.
const RedeemAction = ({ item, storeUuid, token }) => {
  const [redeemState, setRedeemState] = useState("idle");
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    if (redeemState !== "sent" || !expiresAt) return undefined;
    const tick = () => {
      const remaining = Math.round(
        (new Date(expiresAt).getTime() - Date.now()) / 1000,
      );
      if (remaining <= 0) {
        setSecondsLeft(0);
        setRedeemState("expired");
        return;
      }
      setSecondsLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [redeemState, expiresAt]);

  if (!item?.barcode || !storeUuid || !token) return null;

  const handleRedeem = () => {
    if (
      redeemState === "sending" ||
      redeemState === "sent" ||
      TERMINAL_REDEEM_STATES.has(redeemState)
    ) {
      return;
    }
    setRedeemState("sending");
    redeemPromotion({ storeUuid, promotionUuid: item.uuid, token })
      .then((response) => {
        setExpiresAt(response?.data?.expires_at || null);
        setRedeemState("sent");
      })
      .catch((error) => {
        const body = error?.body;
        if (body?.already_redeemed) setRedeemState("already_redeemed");
        else if (body?.max_attempts_reached) setRedeemState("max_attempts");
        else setRedeemState("error");
      });
  };

  return (
    <div style={{ padding: "0 20px 20px" }}>
      {redeemState === "sent" && (
        <div
          style={{
            background: "#ecfdf3",
            color: "#1a7431",
            borderRadius: 10,
            padding: "12px 16px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
            Request sent — show this to the store
          </div>
          {secondsLeft !== null && (
            <div style={{ fontSize: 13, marginTop: 4, opacity: 0.85 }}>
              Store has {secondsLeft}s left to confirm
            </div>
          )}
        </div>
      )}

      {redeemState === "already_redeemed" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            background: "#f3f4f6",
            color: "#374151",
            borderRadius: 10,
            padding: "12px 16px",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
          You&rsquo;ve already redeemed this offer
        </div>
      )}

      {redeemState === "max_attempts" && (
        <div
          style={{
            textAlign: "center",
            background: "#fbe9ea",
            color: "#8f1f2b",
            borderRadius: 10,
            padding: "12px 16px",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          You&rsquo;ve used all your redemption attempts for this offer
        </div>
      )}

      {!TERMINAL_REDEEM_STATES.has(redeemState) && redeemState !== "sent" && (
        <>
          <button
            type="button"
            onClick={handleRedeem}
            disabled={redeemState === "sending"}
            style={{
              width: "100%",
              border: "none",
              borderRadius: 10,
              padding: "12px 16px",
              fontWeight: 700,
              fontSize: 14,
              color: "#fff",
              background: redeemState === "sending" ? "#93c5fd" : "#2563eb",
              cursor: redeemState === "sending" ? "default" : "pointer",
            }}
          >
            {redeemState === "sending" ? "Sending request…" : "Redeem"}
          </button>
          {redeemState === "expired" && (
            <div
              style={{
                color: "#b8860a",
                fontSize: 13,
                marginTop: 6,
                textAlign: "center",
              }}
            >
              The store didn&rsquo;t confirm in time — your request expired.
              Tap Redeem to try again.
            </div>
          )}
          {redeemState === "error" && (
            <div
              style={{
                color: "#cf2637",
                fontSize: 13,
                marginTop: 6,
                textAlign: "center",
              }}
            >
              Couldn&rsquo;t send the request — tap Redeem to try again.
            </div>
          )}
        </>
      )}
    </div>
  );
};

RedeemAction.propTypes = {
  item: PropTypes.object.isRequired,
  storeUuid: PropTypes.string,
  token: PropTypes.string,
};

// "Coupon ticket" popup: banner in the tapped card's colour, headline, expiry,
// code instructions, terms and a scannable barcode.
const PromotionDialog = ({ selected, storeImage, storeUuid, token, onClose }) => {
  const item = selected?.item;
  const conditions = conditionLines(item?.terms);
  const expires = item?.end_at ? formatLongDate(item.end_at) : "";

  return (
    <Dialog
      open={!!selected}
      onClose={onClose}
      fullWidth
      TransitionComponent={SlideUpTransition}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
      PaperProps={{
        sx: {
          margin: 0,
          width: "100%",
          maxWidth: 480,
          maxHeight: "88vh",
          borderRadius: "20px 20px 0 0",
        },
      }}
    >
      {item ? (
        <div
          style={{
            background: "#fff",
            color: "#111827",
            maxHeight: "88vh",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 0" }}>
            <div
              aria-hidden="true"
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "rgba(0,0,0,0.18)",
              }}
            />
          </div>
          <div style={{ position: "relative" }}>
            <div
              style={{
                height: 96,
                backgroundColor: selected.theme.background,
                backgroundImage: DOODLE_BG,
                backgroundRepeat: "repeat",
              }}
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "none",
                background: "rgba(0,0,0,0.35)",
                color: "#fff",
                fontSize: 20,
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ×
            </button>
            <div
              style={{
                position: "absolute",
                left: 20,
                bottom: -28,
                padding: 3,
                background: "#fff",
                borderRadius: "50%",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #eee",
                }}
              >
                {storeImage ? (
                  <img
                    src={storeImage}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <LocalOfferOutlinedIcon
                    sx={{ fontSize: 26, color: "#9ca3af" }}
                  />
                )}
              </div>
            </div>
          </div>

          <div style={{ padding: "36px 20px 4px" }}>
            {item.brand_name ? (
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {item.brand_name}
              </div>
            ) : null}
            <div
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: "#d9480f",
                marginTop: 6,
              }}
            >
              {selected.headline}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6 }}>
              {item.product_name}
              {item.size_variant ? ` (${item.size_variant})` : ""}
            </div>
            {item.description ? (
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                {item.description}
              </div>
            ) : null}
          </div>

          <div style={{ borderTop: "1px solid #e5e7eb", margin: "14px 0" }} />

          {expires ? (
            <div style={{ padding: "0 20px 16px", fontSize: 13 }}>
              <span aria-hidden="true">⏳ </span>Expires {expires}
            </div>
          ) : null}

          {item.promo_code ? (
            <div style={{ padding: "0 20px 16px" }}>
              <div style={sectionTitleStyle}>Details</div>
              <ul style={listStyle}>
                <li style={listItemStyle}>
                  Enter code {item.promo_code} at checkout
                </li>
              </ul>
            </div>
          ) : null}

          {conditions.length > 0 ? (
            <div style={{ padding: "0 20px 16px" }}>
              <div style={sectionTitleStyle}>Terms &amp; conditions</div>
              <ul style={listStyle}>
                {conditions.map((line) => (
                  <li key={line} style={listItemStyle}>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div
            style={{
              borderTop: "1px dashed #e5e7eb",
              padding: 20,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {item.barcode ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 10,
                  padding: 12,
                  maxWidth: "100%",
                  overflowX: "auto",
                }}
              >
                <Barcode
                  value={ensureString(item.barcode)}
                  height={56}
                  width={1.4}
                  fontSize={11}
                />
              </div>
            ) : (
              <p style={{ color: "#6b7280", margin: 0 }}>
                No barcode on file for this item.
              </p>
            )}
          </div>

          <RedeemAction
            key={item.uuid}
            item={item}
            storeUuid={storeUuid}
            token={token}
          />

          {/* Clears the home-indicator area on notched phones so the last
              action isn't flush against it. */}
          <div style={{ height: "max(12px, env(safe-area-inset-bottom))" }} />
        </div>
      ) : null}
    </Dialog>
  );
};

PromotionDialog.propTypes = {
  selected: PropTypes.shape({
    item: PropTypes.object,
    theme: PropTypes.object,
    headline: PropTypes.string,
  }),
  storeImage: PropTypes.string,
  storeUuid: PropTypes.string,
  token: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

const CouponsList = ({
  isLoading = false,
  errorMessage = "",
  hasSelectedBrand = false,
  quickOffersData = null,
  promotionsData = null,
  storeUuid = null,
  token = null,
}) => {
  const offers = useMemo(
    () =>
      normalizeQuickOfferArray(quickOffersData)
        .map(normalizeQuickOfferItem)
        .filter(
          (item) =>
            item.isActive !== false &&
            (item.title ||
              item.promoCode ||
              item.validity ||
              item.content ||
              item.link),
        ),
    [quickOffersData],
  );

  const [selectedPromo, setSelectedPromo] = useState(null);
  const isCompact = useMediaQuery(COMPACT_QUERY);

  const promotionGroups = useMemo(
    () => normalizePromotionGroups(promotionsData),
    [promotionsData],
  );

  if (!hasSelectedBrand) {
    return renderStatusCard(
      <LinkOffOutlinedIcon sx={{ fontSize: 28 }} />,
      "Select a brand to load coupons",
      "Pick a store first, then open this tab to view its quick offers.",
    );
  }

  if (isLoading) {
    return renderStatusCard(
      <LocalOfferOutlinedIcon sx={{ fontSize: 28 }} />,
      "Loading coupons",
      "We are fetching the latest quick offers for this store.",
      true,
    );
  }

  if (errorMessage && promotionGroups.length === 0) {
    return renderStatusCard(
      <LinkOffOutlinedIcon sx={{ fontSize: 28 }} />,
      "Unable to load coupons",
      "The quick offers request failed. Try again or switch to another store.",
    );
  }

  if (offers.length === 0 && promotionGroups.length === 0) {
    return renderStatusCard(
      <LocalOfferOutlinedIcon sx={{ fontSize: 28 }} />,
      "No coupons available",
      "This store does not have any quick offers right now.",
    );
  }

  let offersSection = null;
  if (offers.length > 0) {
    offersSection = isCompact ? (
      // A 3-column table forces sideways scrolling to read code/validity
      // on a phone — stacked cards keep everything in one glance without it.
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {offers.map((offer, index) => (
          <div
            key={offer.id || `coupon-item-${index}`}
            style={{
              border: "1px solid rgba(16, 24, 40, 0.08)",
              borderRadius: 12,
              padding: "12px 14px",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14, color: "#101828" }}>
              {offer.title || "-"}
            </div>
            {offer.content || offer.link ? (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: "#98a2b3",
                }}
                dangerouslySetInnerHTML={{
                  __html: linkifyPlainText(offer.content, offer.link),
                }}
              />
            ) : null}
            {offer.promoCode || offer.validity ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                {offer.promoCode ? (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#344054",
                      background: "#f2f4f7",
                      borderRadius: 999,
                      padding: "4px 10px",
                    }}
                  >
                    {offer.promoCode}
                  </span>
                ) : null}
                {offer.validity ? (
                  <span style={{ fontSize: 12, color: "#667085" }}>
                    {formatValidity(offer.validity)}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    ) : (
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderSpacing: 0,
          }}
        >
          <tbody>
            {offers.map((offer, index) => (
              <tr key={offer.id || `coupon-item-${index}`}>
                <td
                  style={{
                    padding: "12px 8px",
                    fontSize: 14,
                    color: "#101828",
                    verticalAlign: "top",
                    borderBottom: "1px solid rgba(16, 24, 40, 0.08)",
                    width: "46%",
                  }}
                >
                  <div style={{ fontWeight: 500, color: "#101828" }}>
                    {offer.title || "-"}
                  </div>
                  {offer.content || offer.link ? (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 13,
                        lineHeight: 1.45,
                        color: "#98a2b3",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: linkifyPlainText(offer.content, offer.link),
                      }}
                    />
                  ) : null}
                </td>
                <td
                  style={{
                    padding: "12px 8px",
                    fontSize: 14,
                    color: "#475467",
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid rgba(16, 24, 40, 0.08)",
                    width: "22%",
                  }}
                >
                  {offer.promoCode || "-"}
                </td>
                <td
                  style={{
                    padding: "12px 8px",
                    fontSize: 14,
                    color: "#475467",
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid rgba(16, 24, 40, 0.08)",
                    width: "32%",
                  }}
                >
                  {formatValidity(offer.validity)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          maxWidth: 920,
          padding: isCompact ? "0 12px" : "0 16px",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <p
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#1f2937",
            }}
          >
            Coupons & Quick Offers
          </p>
          <p
            style={{
              margin: "6px 0 0",
              fontSize: 14,
              color: "#667085",
            }}
          >
            Available offers and promotions for the selected store.
          </p>
        </div>

        {promotionGroups.length > 0 ? (
          <PromotionGroups
            groups={promotionGroups}
            storeImage={promotionsData?.store_image}
            onSelect={setSelectedPromo}
          />
        ) : null}

        {offersSection}
      </div>
      <PromotionDialog
        selected={selectedPromo}
        storeImage={promotionsData?.store_image}
        storeUuid={storeUuid}
        token={token}
        onClose={() => setSelectedPromo(null)}
      />
    </>
  );
};

CouponsList.propTypes = {
  isLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  hasSelectedBrand: PropTypes.bool,
  quickOffersData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  promotionsData: PropTypes.object,
  storeUuid: PropTypes.string,
  token: PropTypes.string,
};

export default CouponsList;
